/**
 * Pure format-and-validate helpers for common legal-document fields.
 *
 * Each validator returns an object `{ ok, value, message }` where:
 *   - `ok` is true if the input is valid (possibly after normalization)
 *   - `value` is the canonical formatted form (e.g. "123-45-6789" for SSN)
 *     when ok; the original input otherwise
 *   - `message` is a human-readable hint when !ok
 *
 * Validators accept empty values as ok (no format enforcement on empties —
 * that's the empty-field-highlighter's job). Only non-empty inputs are
 * format-checked.
 */

const onlyDigits = (s) => String(s ?? '').replace(/\D+/g, '');

export const validateSSN = (raw) => {
  if (raw == null || String(raw).trim() === '') return { ok: true, value: '' };
  const d = onlyDigits(raw);
  if (d.length !== 9) {
    return { ok: false, value: raw, message: 'SSN should be 9 digits (e.g. 123-45-6789).' };
  }
  return { ok: true, value: `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` };
};

export const validateEIN = (raw) => {
  if (raw == null || String(raw).trim() === '') return { ok: true, value: '' };
  const d = onlyDigits(raw);
  if (d.length !== 9) {
    return { ok: false, value: raw, message: 'EIN should be 9 digits (e.g. 12-3456789).' };
  }
  return { ok: true, value: `${d.slice(0, 2)}-${d.slice(2)}` };
};

export const validatePhone = (raw) => {
  if (raw == null || String(raw).trim() === '') return { ok: true, value: '' };
  const d = onlyDigits(raw);
  if (d.length === 10) {
    return { ok: true, value: `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` };
  }
  if (d.length === 11 && d.startsWith('1')) {
    return { ok: true, value: `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}` };
  }
  return { ok: false, value: raw, message: 'Phone should be 10 digits (e.g. (555) 123-4567).' };
};

export const validateZip = (raw) => {
  if (raw == null || String(raw).trim() === '') return { ok: true, value: '' };
  const d = onlyDigits(raw);
  if (d.length === 5) return { ok: true, value: d };
  if (d.length === 9) return { ok: true, value: `${d.slice(0, 5)}-${d.slice(5)}` };
  return { ok: false, value: raw, message: 'ZIP should be 5 or 9 digits.' };
};

export const validateDate = (raw) => {
  if (raw == null || String(raw).trim() === '') return { ok: true, value: '' };
  const s = String(raw).trim();

  // Accept MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD, M/D/YY, etc.
  // Normalize to MM/DD/YYYY.
  const mdy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (mdy) {
    let [, mm, dd, yyyy] = mdy;
    mm = mm.padStart(2, '0');
    dd = dd.padStart(2, '0');
    if (yyyy.length === 2) {
      const yNum = parseInt(yyyy, 10);
      yyyy = (yNum >= 50 ? 1900 + yNum : 2000 + yNum).toString();
    }
    if (!isCalendarValid(+mm, +dd, +yyyy)) {
      return { ok: false, value: raw, message: 'Date does not exist on the calendar.' };
    }
    return { ok: true, value: `${mm}/${dd}/${yyyy}` };
  }

  const ymd = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (ymd) {
    const [, yyyy, mm, dd] = ymd;
    if (!isCalendarValid(+mm, +dd, +yyyy)) {
      return { ok: false, value: raw, message: 'Date does not exist on the calendar.' };
    }
    return { ok: true, value: `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yyyy}` };
  }

  return {
    ok: false,
    value: raw,
    message: 'Use MM/DD/YYYY (e.g. 01/15/1975).',
  };
};

const isCalendarValid = (m, d, y) => {
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  const daysInMonth = new Date(y, m, 0).getDate();
  return d <= daysInMonth;
};

/**
 * Classify a template field by its name/key so we know which validator to apply.
 * Returns one of: 'ssn' | 'ein' | 'phone' | 'zip' | 'date' | null.
 */
// Word-boundary token matcher — a name-part is present iff it sits at a
// string boundary or between non-alphanumeric separators (_, -, space, etc.).
// This is stricter than `\b` alone (which treats digits as word chars) and
// correctly rejects substring-only matches like "update_notes" / "hotel" /
// "candidate_id" that tripped up the previous `/date/` and `/tel(ephone)?/`
// patterns.
const tokenRe = (token) => new RegExp(`(^|[^a-z0-9])${token}($|[^a-z0-9])`);
const hasToken = (haystack, ...tokens) => tokens.some((t) => tokenRe(t).test(haystack));

export const classifyFieldByName = (name) => {
  if (!name) return null;
  const n = String(name).toLowerCase();

  if (hasToken(n, 'ssn') || /social.?security|social.?sec/.test(n)) return 'ssn';
  if (hasToken(n, 'ein') || /federal.?tax.?id|fed.?id|employer.?id/.test(n)) return 'ein';
  if (hasToken(n, 'phone', 'telephone', 'tel', 'mobile', 'cell', 'fax')) return 'phone';
  if (hasToken(n, 'zip', 'postal')) return 'zip';

  // Date classifier — be conservative. Tokens like `signed`, `issued`,
  // `expires` are common in legal templates as standalone words for
  // *names of the signer / recipient* (e.g. `signed_by`, `issued_to`),
  // not for dates (Bugbot #66, Medium). Only auto-classify a field as
  // a date when it explicitly references a date concept:
  //   - 'date' as a tokenized word (signature_date, date_of_birth,
  //     expiration_date, ...) — token boundaries reject substring
  //     matches like "update_notes" / "candidate_id"
  //   - the unambiguous abbreviations: dob, dod
  //   - the compound words 'birthday' / 'birthdate'
  // Fields like `signed_by`, `issued_to`, `expires_on` are deliberately
  // left unclassified — the user can type freely and we surface no
  // spurious "Use MM/DD/YYYY" warnings.
  if (hasToken(n, 'date', 'dob', 'dod', 'birthday', 'birthdate')) return 'date';

  return null;
};

export const VALIDATORS = {
  ssn: validateSSN,
  ein: validateEIN,
  phone: validatePhone,
  zip: validateZip,
  date: validateDate,
};

export const validateByFieldName = (fieldName, value) => {
  const kind = classifyFieldByName(fieldName);
  if (!kind) return { ok: true, value };
  const fn = VALIDATORS[kind];
  if (!fn) return { ok: true, value };
  return fn(value);
};
